"use client";

import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from "react";
import { Play } from "lucide-react";
import { site } from "@/config/site";
import { AuroraField } from "./AuroraField";
import { Reveal } from "@/components/motion/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";

export type VideoContent = {
  eyebrow: string;
  headline: string;
  titlePlaceholder: string;
  label: string;
};

export function VideoSection({ content }: { content: VideoContent }) {
  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (playing) {
      iframeRef.current?.focus();
    }
  }, [playing]);

  const src = `https://www.youtube-nocookie.com/embed/${site.youtubeId}?autoplay=1&rel=0&modestbranding=1`;
  const plainSrc = `https://www.youtube-nocookie.com/embed/${site.youtubeId}`;

  /** Přehrávač je propojený s kanonickou adresou videa (Ctrl/Cmd/middle-click otevře YouTube). */
  const startPlayback = () => setPlaying(true);

  const onPlayClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    startPlayback();
  };

  const onPlayKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      startPlayback();
    }
  };

  return (
    <section
      id="video"
      aria-labelledby="video-title"
      className="relative overflow-hidden bg-ink-900 py-24 md:py-32"
    >
      <AuroraField variant="soft" className="opacity-60" />

      <div className="container-page relative z-10">
        <Reveal>
          <div className="flex flex-col items-center text-center">
            <SectionLabel>{content.eyebrow}</SectionLabel>
            <h2 id="video-title" className="h2-display mt-6 text-text">
              {content.headline}
            </h2>
            <p className="mono-label mt-4 text-text-faint">{content.titlePlaceholder}</p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mx-auto mt-12 max-w-[1040px]">
            <div className="relative aspect-[16/9] w-full overflow-hidden rounded-xl border border-[var(--color-hairline)] bg-ink-700 md:aspect-[21/9]">
              {playing ? (
                <iframe
                  ref={iframeRef}
                  src={src}
                  title="Video o reklamačním řádu pro B2B"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="absolute inset-0 size-full"
                />
              ) : (
                <>
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[radial-gradient(60%_120%_at_50%_0%,rgba(26,86,219,0.22),transparent_70%)]"
                  />
                  <noscript>
                    <iframe
                      src={plainSrc}
                      title="Video o reklamačním řádu pro B2B"
                      loading="lazy"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                      className="absolute inset-0 size-full"
                    />
                  </noscript>
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                    <a
                      href={site.youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Přehrát video"
                      onClick={onPlayClick}
                      onKeyDown={onPlayKeyDown}
                      className="keycap keycap-neutral flex size-16 items-center justify-center rounded-full p-0"
                    >
                      <Play size={24} className="translate-x-0.5 text-ink-700" fill="currentColor" />
                    </a>
                    <span className="mono-label text-text-muted">{content.label}</span>
                  </div>
                </>
              )}
            </div>
            <p aria-live="polite" className="sr-only">
              {playing ? "Video se přehrává." : "Video je připraveno ke spuštění."}
            </p>
          </div>
        </Reveal>
      </div>

      {/* Trh papíru: ink → paper */}
      <div className="tear-edge" />
    </section>
  );
}
